import React, {
	// eslint-disable-next-line jira/restricted/react
	PureComponent,
	type ComponentType,
	type ReactElement,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import type { TimelineDuration, TimelineWindow, TimelineMode } from '../../../../model/timeline';
import {
	CHART_OVERLAY_Z_INDEX,
	CHART_OVERLAY_VERTICAL_SYNC_Z_INDEX,
	LEGEND_Z_INDEX,
} from '../../../../styles/z-index.tsx';
import { getColumnDurationsByDateRange } from '../../../../utils/columns.tsx';
import withSynchronizedScroll from '../../common/context/scroll-synchronizer/consumer/index.tsx';
import type { TableCommonProps, ChartItem } from '../../common/types';
import getScrollbarSize from '../../common/utils/get-scrollbar-size';
import { getTimelineWidth, getPositionFromDateFn } from '../../common/utils/timeline.tsx';
import type { APIProps as BarProps } from '../bar/view.tsx';
import type { APIProps as ChartIndicatorsProps } from '../chart-indicators/view.tsx';
import type { APIProps as ChartItemProps } from '../chart-item';
import type { APIProps as OverlayProps } from '../chart-overlay';
import type { APIProps as ChartHeaderProps } from './chart-header/view.tsx';
import type { APIProps as ColumnsOverlayProps } from './columns-overlay';
import CustomScrollbars from './custom-scrollbars/view.tsx';
import type { APIProps as HeaderCellsProps } from './header-cells';
import type { APIProps as ItemsProps } from './items';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HiddenScrollbarPanel = styled.div<{ scrollbarSize: number }>(
	{
		position: 'absolute',
		top: 0,
		right: 0,
		left: 0,
		bottom: 0,
		overflow: 'scroll',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ scrollbarSize }: { scrollbarSize: number }) =>
		scrollbarSize > 0 && `right: -${scrollbarSize}px; bottom: -${scrollbarSize}px;`,
);

// @ts-expect-error - Argument of type 'ComponentType<{ scrollbarSize: number; } & ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps>' is not assignable to parameter of type 'ComponentType<Props>'.
const SynchronizedScrollbar = withSynchronizedScroll(HiddenScrollbarPanel, {
	syncAxis: 'y',
	isCompiledComponent: true,
});

type ChartComponents = {
	ChartItem: ComponentType<ChartItemProps>;
	ChartOverlay: ComponentType<OverlayProps>;
	Bar: ComponentType<BarProps>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Legend: ComponentType<Record<any, any>>;
	ChartIndicators: ComponentType<ChartIndicatorsProps>;
};

type APIProps = {
	isHighlightToday: boolean;
	isHideScrollbar: boolean;
} & TableCommonProps & {
		items: ChartItem[];
		components: ChartComponents;
		timelineMode: TimelineMode;
		timelineDuration: TimelineDuration;
		timelineWindow: TimelineWindow;
		timelineDefaultPosition: number;
		today: Date;
		listWidth: number;
		onTimelineUpdated: () => void;
	};

type Props = APIProps & {
	ChartHeader: ComponentType<ChartHeaderProps>;
	HeaderCells: ComponentType<HeaderCellsProps>;
	ColumnsOverlay: ComponentType<ColumnsOverlayProps>;
	Items: ComponentType<ItemsProps>;
};

// eslint-disable-next-line jira/react/no-class-components
class Chart extends PureComponent<Props> {
	// go/jfe-eslint
	// eslint-disable-next-line react/sort-comp
	static whyDidYouRender = true;

	static defaultProps = {
		isSelectEnabled: true,
		isHoverEnabled: false,
		isHighlightToday: true,
		isHideScrollbar: false,
		items: [],
		expandedItems: {},
		onItemClick: noop,
		onTimelineUpdated: undefined,
	};

	// invalidate cached ColumnsOverlay when today changes
	UNSAFE_componentWillReceiveProps(nextProps: Props) {
		if (this.props.today !== nextProps.today) {
			this.columnsOverlayElements = {};
		}
	}

	columnsOverlayElements: Partial<
		Record<TimelineMode, ReactElement<ComponentProps<ComponentType<ColumnsOverlayProps>>>>
	> = {};

	renderHeader() {
		const {
			today,
			timelineMode,
			timelineDuration,
			isHighlightToday,
			ChartHeader,
			onTimelineUpdated,
			timelineDefaultPosition,
			HeaderCells,
			timelineWindow,
		} = this.props;

		const columnDurations = getColumnDurationsByDateRange(timelineMode, timelineDuration);
		return (
			<ChartHeader
				width={getTimelineWidth(timelineMode, timelineDuration)}
				isHighlightToday={isHighlightToday}
				columnDurations={columnDurations}
				timelineDuration={timelineDuration}
				timelineMode={timelineMode}
				timelineWindow={timelineWindow}
				onTimelineUpdated={onTimelineUpdated}
				today={today}
				timelineDefaultPosition={timelineDefaultPosition}
				HeaderCells={HeaderCells}
			/>
		);
	}

	renderTimeline() {
		const {
			items,
			expandedItems,
			activeLevel,
			timelineMode,
			timelineWindow,
			timelineDuration,
			timelineDefaultPosition,
			components,
			isSelectEnabled,
			isHoverEnabled,
			onItemClick,
			ColumnsOverlay,
			Items,
			listWidth,
			itemHeight,
		} = this.props;

		if (!this.columnsOverlayElements[timelineMode]) {
			const columnDurations = getColumnDurationsByDateRange(timelineMode, timelineDuration);
			this.columnsOverlayElements[timelineMode] = (
				<ColumnsOverlay
					timelineWindow={timelineWindow}
					timelineDuration={timelineDuration}
					columnDurations={columnDurations}
				/>
			);
		}

		return (
			<Timeline width={getTimelineWidth(timelineMode, timelineDuration)}>
				{this.columnsOverlayElements[timelineMode]}
				<OverlayContainer>
					<components.ChartOverlay
						timelineWindow={timelineWindow}
						timelineMode={timelineMode}
						timelineDuration={timelineDuration}
					/>
				</OverlayContainer>
				<ItemsContainer>
					<Items
						items={items}
						expandedItems={expandedItems}
						listWidth={listWidth}
						itemHeight={itemHeight}
						activeLevel={activeLevel}
						timelineMode={timelineMode}
						timelineDuration={timelineDuration}
						timelineDefaultPosition={timelineDefaultPosition}
						components={{
							ChartItem: components.ChartItem,
							Bar: components.Bar,
						}}
						isSelectEnabled={isSelectEnabled}
						isHoverEnabled={isHoverEnabled}
						onItemClick={onItemClick}
					/>
					{items.length > 0 && <ItemsBottomPadding />}
				</ItemsContainer>
			</Timeline>
		);
	}

	render() {
		const {
			timelineDuration,
			timelineDefaultPosition,
			isHideScrollbar,
			components,
			items,
			timelineMode,
		} = this.props;
		const getPositionFromDate = getPositionFromDateFn(
			timelineDuration.startDate,
			timelineDuration.endDate,
			timelineMode,
		);
		const scrollbarSize = getScrollbarSize();
		return (
			<ChartContainer>
				<BorderShadow />
				{this.renderHeader()}
				<ChartBodyContainer>
					<ChartOverlayVerticalSyncContainer>
						{/* @ts-expect-error - Type '{ children: (false | Element | Element[])[]; scrollbarSize: number; }' is not assignable to type 'IntrinsicAttributes & ((Props | Omit<Props & RefAttributes<Component<Props, any, any>>, "ref">) & RefAttributes<...>)'. */}
						<SynchronizedScrollbar scrollbarSize={scrollbarSize}>
							{items.map((item) => (
								<components.ChartIndicators
									key={item.id}
									id={item.id}
									getPositionFromDate={getPositionFromDate}
								/>
							))}
							{items.length > 0 && <ItemsBottomPadding invisible />}
						</SynchronizedScrollbar>
					</ChartOverlayVerticalSyncContainer>
					<CustomScrollbars
						timelineDefaultPosition={timelineDefaultPosition}
						isHideScrollbar={isHideScrollbar}
					>
						{this.renderTimeline()}
					</CustomScrollbars>
					<LegendContainer>
						<components.Legend />
					</LegendContainer>
				</ChartBodyContainer>
			</ChartContainer>
		);
	}
}

interface ItemsBottomPaddingProps {
	invisible?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemsBottomPadding = styled.div<ItemsBottomPaddingProps>({
	height: '88px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.text.inverse', colors.N0),
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props: ItemsBottomPaddingProps) => (props.invisible ? 0 : 1),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartContainer = styled.div({
	height: '100%',
	width: '100%',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `0 ${borderRadius}px ${borderRadius}px 0`,
	minHeight: 0,
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartBodyContainer = styled.div({
	position: 'relative',
	flexGrow: 1,
});

interface TimelineProps {
	width: number;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Timeline = styled.div<TimelineProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ width }: TimelineProps) => ({ width: `${width}px` }),
	position: 'relative',
	minHeight: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OverlayContainer = styled.div({
	position: 'absolute',
	pointerEvents: 'none',
	top: 0,
	left: 0,
	width: '100%',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: CHART_OVERLAY_Z_INDEX,
});

// RLSS-2812 replace color.background.neutral into color.background.accent.gray.subtlest
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemsContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral', colors.N50A),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegendContainer = styled.div({
	position: 'absolute',
	bottom: token('space.150', '12px'),
	right: token('space.150', '12px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: LEGEND_Z_INDEX,
	maxHeight: '100%',
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartOverlayVerticalSyncContainer = styled.div({
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: CHART_OVERLAY_VERTICAL_SYNC_Z_INDEX,
	width: '100%',
	height: '100%',
	overflow: 'hidden',
	pointerEvents: 'none',
});

// RLSS-2812 replace the first color.background.neutral to color.background.accent.gray.subtle
//           replace the second color.background.neutral to color.background.accent.gray.subtlest
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BorderShadow = styled.div({
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: CHART_OVERLAY_VERTICAL_SYNC_Z_INDEX,
	width: '3px',
	top: '0px',
	bottom: '0px',
	height: '100%',
	borderRight: `1px solid ${token('color.border', 'rgba(9, 30, 66, 0.1)')}`,
	background: `linear-gradient( 270deg, ${token('color.background.neutral.bold', 'rgba(9, 30, 66, 1)')} -340.65%, ${token('color.background.neutral', 'rgba(9, 30, 66, 0.551037)')} -207.28%, ${token('color.background.neutral', 'rgba(9, 30, 66, 0.173565)')} -96.18%, ${token('color.background.neutral.subtle', 'rgba(9, 30, 66, 0.0001)')} 97.56% )`,
	transform: 'matrix(-1, 0, 0, 1, 0, 0)',
});

export default Chart;
export type { APIProps };
